import React from "react";
import { ListTodo, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/use-media-query";
import { isDesktopSize } from "utils/is-desktop";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ListCard } from "modules/lists/components/ListCard";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { ItemGroup } from "@/components/ui/item";
import { useLists } from "modules/lists/context/ListsContext";

interface ListsDashboardProps {
  className?: string;
}

/**
 * Dashboard component that displays all user lists in a responsive grid.
 * Shows a desktop-only "create list" card as the first grid item and a
 * sticky-bottom FAB on mobile. Consumer must be wrapped in a ListsProvider.
 */
const ListsDashboard: React.FC<ListsDashboardProps> = ({ className }) => {
  const { lists, createList, deleteList } = useLists();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(isDesktopSize);

  /** Creates a list in the DB and navigates to its detail in edit mode. */
  const handleCreate = async () => {
    const newList = await createList("Nueva lista", "");
    navigate(`/lists/${newList.id}`, { state: { isNew: true } });
  };

  if(lists.length === 0){
    return(
    <Empty>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <ListTodo />
        </EmptyMedia>
        <EmptyTitle>Aún no tienes listas creadas</EmptyTitle>
        <EmptyDescription>
        Crea tu primera lista para optimizar tus compras y mantenerlas organizadas.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent className="flex-row justify-center gap-2">
        <Button size={"lg"} className="px-6 text-base" onClick={handleCreate}>Crea tu primera lista</Button>
      </EmptyContent>
    </Empty>      
    );
  }

  return (
    <div className={cn("w-full", { "mx-auto w-full": isDesktop }, className)}>
      <ItemGroup className={cn("flex flex-row gap-4 flex-wrap flex-start",{
          "grid grid-cols-3 justify-stretch": isDesktop,  
          "flex-column": !isDesktop     
      })}>
        {lists.length === 0 ? (
          <></>
        ) : (
          lists.map((list) => (
            <ListCard
              key={list.id}
              list={list}
              className={cn("w-full", { "": isDesktop })}
              isSelected={false}
              onSelect={() => navigate(`/lists/${list.id}`)}
              onEdit={() => navigate(`/lists/${list.id}`)}
              onDelete={() => deleteList(list.id)}
            />
          ))
        )}
      </ItemGroup>
    </div>
  );
};

/** Internal desktop-only "create list" card — first grid item, styling matches ListCard. */
const CreateListCard: React.FC<{ onClick: () => void, className?: string }> = ({ onClick, className }) => (
  <Card
    className={cn("pb-0 gap-3 pt-2 px-0 cursor-pointer", className)}
    onClick={onClick}
  >
    <CardContent className="flex flex-col items-center justify-center h-full min-h-[180px]">
      <div className="w-full">
          <p className="text-center font-bold text-lg">Crea tu Lista</p>
          <p className="text-center text-xs text-muted-foreground">Agrega una nueva lista para organizar tus compras</p>
      </div>      
      <Plus className="h-16 w-16 text-muted-foreground" />
    </CardContent>
  </Card>
);

export { ListsDashboard };
